import { FC, MutableRefObject, PropsWithChildren, useCallback, useMemo, useRef } from 'react';
import Webcam from 'react-webcam';

import { CAPTURE_VIDEO_CONSTRAINTS } from '../capture-constraints';

import { DamageType, isGensetDamage } from 'types';
import { ImageOverlay } from 'utils';

import { useDamageOptions } from 'contexts/damage-options';
import { useDamage } from '.';
import { useNewTransaction } from '../new-transaction.context';

const GensetDialogWrapper: FC<
  PropsWithChildren<{ className?: string; back?: () => void; next?: () => void }>
> = props => {
  return (
    <div>
      <div className="d-flex bg-primary g-3 p-1">
        <button type="button" className="btn-back d-flex g-2" onClick={props.back} disabled={!props.back}>
          <span className="material-icons icon-large">arrow_back</span>
          <span>BACK</span>
        </button>
        <h4 className="flex-grow-1 text-center">GENSET DAMAGE SELECTION</h4>
        <button type="button" className="btn-back d-flex g-2" onClick={props.next} disabled={!props.next}>
          <span>NEXT</span>
          <span className="material-icons icon-large">arrow_forward</span>
        </button>
      </div>
      <div className={`dialog-content ${props.className ?? ''} p-5`}>{props.children}</div>
    </div>
  );
};

const DamageImage: FC<{ closeDialog: () => void }> = () => {
  const { current, capture, setCapture, setImage } = useDamage();
  const { container, chassis, genset, bulk } = useNewTransaction();
  const webcamRef = useRef<Webcam>(null);

  const handleCapture = useCallback(async () => {
    const text = bulk
      ? chassis.unitNumber ?? ''
      : [container.unitNumber ?? '', chassis.unitNumber ?? ' ', genset.unitNumber ?? ' '].join(' ');
    ImageOverlay.withText(text + ' ' + new Date().toLocaleString())
      .withSetter(setImage)
      .call(Webcam.prototype.getScreenshot.call(webcamRef.current));
  }, [setImage, bulk, container.unitNumber, chassis.unitNumber, genset.unitNumber]);

  return !capture ? null : (
    <GensetDialogWrapper
      className="webcam"
      back={() => setCapture(false)}
      next={!current.imageBase64 ? undefined : () => setCapture(false)}
    >
      {!current.imageBase64 ? (
        <div className="d-flex flex-column">
          <Webcam
            width="100%"
            ref={webcamRef}
            videoConstraints={CAPTURE_VIDEO_CONSTRAINTS}
            screenshotFormat="image/jpeg"
          />
          <button type="button" className="btn-close" onClick={handleCapture}>
            <span className="material-icons">circle</span>
          </button>
        </div>
      ) : (
        <div className="d-flex flex-column">
          <img alt="preview" width="100%" src={current.imageBase64} />
          <button type="button" className="btn-close" onClick={() => setImage(undefined)}>
            <span className="material-icons">rotate_left</span>
          </button>
        </div>
      )}
    </GensetDialogWrapper>
  );
};

const DamageTypeSelection: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { current, setCapture, setCurrent, submit } = useDamage();
  const { gensetDamageList } = useDamageOptions();

  const damageLocationRef: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const damageTypeRef: MutableRefObject<HTMLSelectElement | null> = useRef(null);

  const handleSubmit = () => {
    if (!current.location) {
      damageLocationRef.current?.setCustomValidity('Damage location required');
      damageLocationRef.current?.reportValidity();
    } else if (!current.damageType) {
      damageTypeRef.current?.setCustomValidity('Damage type required');
      damageTypeRef.current?.reportValidity();
    } else {
      damageLocationRef.current?.setCustomValidity('');
      damageTypeRef.current?.setCustomValidity('');
      submit();
      closeDialog();
    }
  };

  return !isGensetDamage(current) ? null : (
    <GensetDialogWrapper back={closeDialog} className="damage-type">
      <h5 className="mb-5 text-center text-primary">GENSET</h5>
      <div className="grid-3 p-3 auto-rows">
        <div className="damage-input col-span-2">
          <input
            type="text"
            className="w-100"
            value={current.location ?? ''}
            onChange={e => setCurrent({ ...current, location: e.target.value.toUpperCase() })}
            ref={node => {
              node?.toggleAttribute('blank', !current.location); /* label transform */
              damageLocationRef.current = node;
            }}
          />
          <label>DAMAGE LOCATION *</label>
        </div>
        <div className="row-span-2 h-100 w-100">
          {!current.imageBase64 ? (
            <button
              type="button"
              className="btn-outline justify-content-center h-100 w-100 grid-1 text-primary"
              onClick={() => setCapture(true)}
            >
              <span className="material-icons image">camera_alt</span>
              <span className="white-space-nowrap text-base">TAKE PHOTO</span>
            </button>
          ) : (
            <button
              type="button"
              className="btn-outline justify-content-center text-info-main h-100 w-100 grid-1"
              onClick={() => setCapture(true)}
            >
              <span className="material-icons image">image</span>
              <span className="white-space-nowrap text-base">VIEW PHOTO</span>
            </button>
          )}
        </div>
        <div className="damage-input col-span-2">
          <select
            className="w-100"
            value={current.damageType ?? ''}
            onChange={e => setCurrent(p => ({ ...p, damageType: (e.target.value as DamageType) || undefined }))}
            ref={node => {
              node?.toggleAttribute('blank', !current.damageType); /* label transform */
              damageTypeRef.current = node;
            }}
          >
            <option value="" />
            {gensetDamageList.map((damageType: string) => (
              <option key={damageType} value={damageType}>
                {damageType}
              </option>
            ))}
          </select>
          <label>DAMAGE TYPE *</label>
        </div>
        <div className="damage-input col-span-3 row-span-2 h-100">
          <textarea
            className="h-100 w-100"
            ref={node => node?.toggleAttribute('blank', !current.notes)}
            value={current.notes}
            onChange={e => setCurrent(p => ({ ...p, notes: e.target.value }))}
          />
          <label>GENSET REPAIR NOTES</label>
        </div>
        <button className="btn-outline col-start-2 align-self-end cancel" type="button" onClick={closeDialog}>
          CANCEL
        </button>
        <button className="btn-primary col-start-3 align-self-end" type="button" onClick={handleSubmit}>
          SUBMIT
        </button>
      </div>
    </GensetDialogWrapper>
  );
};

const GensetDialogContent: FC<{ closeDialog: () => void }> = ({ closeDialog }) => {
  const { capture, current } = useDamage();

  const Selection: FC<{ closeDialog: () => void }> = useMemo(() => {
    return !isGensetDamage(current) ? () => null : !capture ? DamageTypeSelection : DamageImage;
  }, [capture, current]);

  return <Selection closeDialog={closeDialog} />;
};

export default GensetDialogContent;
